import React, { useState, useEffect } from "react";
import SuperAdminLayout from "./SuperAdminLayout";
import { fetchGlobalSubscriptions, fetchDeliveryStats, fetchStats } from "@/services/superadminApi";
import { CreditCard, Receipt, TrendingUp, AlertCircle, FileText, Settings, Search, Filter } from 'lucide-react';
import { CardGlass, Badge, Button, StatCard } from "@/components/ui/PremiumComponents";
import Loader from "@/components/ui/Loader";

export default function SuperAdminBilling() {
    const [activeTab, setActiveTab] = useState('subscriptions');
    const [loading, setLoading] = useState(true);
    const [subs, setSubs] = useState([]);
    const [deliveryStats, setDeliveryStats] = useState({});
    const [globalStats, setGlobalStats] = useState({});
    const [search, setSearch] = useState("");

    const loadData = async () => {
        setLoading(true);
        try {
            const [s, d, g] = await Promise.all([
                fetchGlobalSubscriptions(),
                fetchDeliveryStats().catch(() => ({})),
                fetchStats().catch(() => ({}))
            ]);
            setSubs(s?.subscriptions || []);
            setDeliveryStats(d || {});
            setGlobalStats(g || {});
        } catch (error) {
            console.error("Error loading billing data", error);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => { loadData(); }, []);

    const filteredSubs = subs.filter(s =>
        s.company_id?.toLowerCase().includes(search.toLowerCase()) ||
        s.company_name?.toLowerCase().includes(search.toLowerCase())
    );

    return (
        <SuperAdminLayout>
            <div className="space-y-8 max-w-[1200px] mx-auto">
                <div className="flex justify-between items-end">
                    <div>
                        <h1 className="text-4xl font-black text-white tracking-tight mb-2">Facturación y Suscripciones</h1>
                        <p className="text-white/50 font-bold uppercase tracking-widest text-xs">Control de ingresos y planes</p>
                    </div>
                    <Button onClick={loadData} variant="secondary" size="sm">Sincronizar Datos</Button>
                </div>

                {/* KPIs */}
                <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
                    <StatCard
                        label="MRR Estimado"
                        value={`€${globalStats.mrr_eur || 0}`}
                        icon={<TrendingUp />}
                        trend="+12%"
                    />
                    <StatCard
                        label="Suscripciones Activas"
                        value={globalStats.subscriptions?.total || 0}
                        icon={<CreditCard />}
                    />
                    <StatCard
                        label="Platform Fee Hoy"
                        value={`€${deliveryStats.platform_income_today || 0}`}
                        icon={<Receipt />}
                        trend="+5%"
                    />
                    <StatCard
                        label="Pagos Fallidos"
                        value="0"
                        icon={<AlertCircle />}
                        theme="danger"
                    />
                </div>

                {/* Tabs */}
                <div className="flex gap-4 p-1 glass-effect w-fit rounded-2xl border border-white/10">
                    {['subscriptions', 'revenue', 'settings'].map(tab => (
                        <button
                            key={tab}
                            onClick={() => setActiveTab(tab)}
                            className={`px-6 py-2.5 rounded-xl text-sm font-black transition-all ${activeTab === tab ? 'bg-primary text-white shadow-lg shadow-primary/20' : 'text-white/40 hover:text-white/80'
                                }`}
                        >
                            {tab.charAt(0).toUpperCase() + tab.slice(1)}
                        </button>
                    ))}
                </div>

                {loading ? (
                    <div className="py-20 flex justify-center"><Loader /></div>
                ) : (
                    <div className="space-y-6">
                        {activeTab === 'subscriptions' && (
                            <CardGlass className="p-0 overflow-hidden">
                                <div className="p-6 border-b border-white/5 flex justify-between items-center">
                                    <div className="relative w-72">
                                        <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-white/40" />
                                        <input
                                            type="text"
                                            placeholder="Buscar restaurante..."
                                            className="w-full bg-white/5 border border-white/10 rounded-xl py-2 pl-10 pr-4 text-sm outline-none focus:ring-2 ring-primary/20"
                                            value={search}
                                            onChange={(e) => setSearch(e.target.value)}
                                        />
                                    </div>
                                    <Badge status="info">Total: {subs.length}</Badge>
                                </div>
                                <div className="overflow-x-auto">
                                    <table className="w-full text-left">
                                        <thead>
                                            <tr className="bg-white/5 text-[10px] uppercase font-black text-white/40 tracking-widest">
                                                <th className="px-6 py-4">Restaurante</th>
                                                <th className="px-6 py-4">Plan</th>
                                                <th className="px-6 py-4">Estado</th>
                                                <th className="px-6 py-4">Siguiente Pago</th>
                                                <th className="px-6 py-4 text-right">Acciones</th>
                                            </tr>
                                        </thead>
                                        <tbody className="divide-y divide-white/5">
                                            {filteredSubs.map(s => (
                                                <tr key={s.company_id} className="hover:bg-white/[0.02] transition-colors group">
                                                    <td className="px-6 py-4">
                                                        <div className="font-bold text-white group-hover:text-primary transition-colors">{s.company_name || s.company_id}</div>
                                                        <div className="text-[10px] text-white/30 font-mono uppercase">{s.company_id}</div>
                                                    </td>
                                                    <td className="px-6 py-4">
                                                        <Badge variant="outline">{s.plan_id?.replace('plan_', '').toUpperCase() || 'MOCK'}</Badge>
                                                    </td>
                                                    <td className="px-6 py-4">
                                                        <Badge status={s.status === 'active' ? 'success' : s.status === 'trial' ? 'info' : 'danger'}>
                                                            {s.status?.toUpperCase()}
                                                        </Badge>
                                                    </td>
                                                    <td className="px-6 py-4 text-sm text-white/60">
                                                        {s.next_billing ? new Date(s.next_billing).toLocaleDateString() : '—'}
                                                    </td>
                                                    <td className="px-6 py-4 text-right">
                                                        <button className="p-2 hover:bg-white/10 rounded-lg text-white/40 hover:text-white transition-colors">
                                                            <FileText size={18} />
                                                        </button>
                                                    </td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                            </CardGlass>
                        )}

                        {activeTab === 'revenue' && (
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                <CardGlass className="p-8">
                                    <h3 className="text-xl font-black text-white mb-6">Resumen de Comisiones (Platform Fee)</h3>
                                    <div className="space-y-6">
                                        <div className="flex justify-between items-end border-b border-white/5 pb-4">
                                            <span className="text-white/40 font-bold uppercase text-xs">Acumulado Hoy</span>
                                            <span className="text-3xl font-black text-white">€{deliveryStats.platform_income_today || 0}</span>
                                        </div>
                                        <div className="flex justify-between items-end border-b border-white/5 pb-4 text-white/60">
                                            <span className="text-white/40 font-bold uppercase text-xs">Retenciones Rider (MOCK)</span>
                                            <span className="text-xl font-bold italic">- €42.50</span>
                                        </div>
                                        <div className="flex justify-between items-end pt-4">
                                            <span className="text-primary font-black uppercase text-sm">Neto Plataforma</span>
                                            <span className="text-3xl font-black text-primary">€{deliveryStats.platform_income_today || 0}</span>
                                        </div>
                                    </div>
                                    <div className="mt-8 p-4 bg-primary/5 rounded-2xl border border-primary/10 flex gap-4 items-center">
                                        <AlertCircle className="text-primary flex-shrink-0" size={20} />
                                        <p className="text-xs text-white/70 leading-relaxed">
                                            Estos valores reflejan el <strong>Platform Fee</strong> configurado en los pedidos cerrados de la red global de delivery.
                                        </p>
                                    </div>
                                </CardGlass>

                                <CardGlass className="p-8">
                                    <h3 className="text-xl font-black text-white mb-6">Suscripciones por Plan</h3>
                                    <div className="space-y-4">
                                        {['Basic', 'Professional', 'Enterprise'].map(plan => {
                                            const count = subs.filter(s => s.plan_id === `plan_${plan.toLowerCase()}`).length;
                                            const pct = subs.length ? (count / subs.length) * 100 : 0;
                                            return (
                                                <div key={plan} className="space-y-2">
                                                    <div className="flex justify-between text-xs font-black uppercase">
                                                        <span className="text-white/60">{plan}</span>
                                                        <span className="text-white">{count} tenants</span>
                                                    </div>
                                                    <div className="h-2 w-full bg-white/5 rounded-full overflow-hidden">
                                                        <div
                                                            className="h-full bg-primary transition-all duration-1000"
                                                            style={{ width: `${pct}%` }}
                                                        />
                                                    </div>
                                                </div>
                                            );
                                        })}
                                    </div>
                                </CardGlass>
                            </div>
                        )}

                        {activeTab === 'settings' && (
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                                <CardGlass className="p-8 space-y-6">
                                    <div className="flex items-center gap-4 border-b border-white/5 pb-6">
                                        <div className="w-12 h-12 rounded-2xl bg-indigo-500/10 flex items-center justify-center">
                                            <Settings className="text-indigo-400" size={24} />
                                        </div>
                                        <div>
                                            <h3 className="text-xl font-black text-white">Stripe Connect</h3>
                                            <p className="text-sm text-white/40">Pasarela de pagos principal</p>
                                        </div>
                                    </div>
                                    <div className="p-4 bg-indigo-500/5 rounded-2xl border border-indigo-500/10 text-[13px] text-white/80 leading-relaxed italic">
                                        Plataforma conectada actualmente en modo TEST. Los cargos reales requieren validación de KYC en el Dashboard de Stripe.
                                    </div>
                                    <div className="space-y-4 pt-4">
                                        <Button className="w-full justify-between" variant="secondary">
                                            Ver Dashboard de Stripe
                                            <TrendingUp size={16} />
                                        </Button>
                                    </div>
                                </CardGlass>

                                <CardGlass className="p-8 space-y-6">
                                    <div className="flex items-center gap-4 border-b border-white/5 pb-6">
                                        <div className="w-12 h-12 rounded-2xl bg-primary/10 flex items-center justify-center">
                                            <Receipt className="text-primary" size={24} />
                                        </div>
                                        <div>
                                            <h3 className="text-xl font-black text-white">Configuración Fiscal</h3>
                                            <p className="text-sm text-white/40">Impuestos y Series</p>
                                        </div>
                                    </div>
                                    <div className="space-y-4">
                                        <div className="grid grid-cols-2 gap-4">
                                            <div className="space-y-2">
                                                <label className="text-[10px] font-black uppercase text-white/40">IVA Predeterminado</label>
                                                <input type="text" value="21%" disabled className="w-full bg-white/5 border border-white/10 rounded-xl py-2 px-4 text-white/40 text-sm italic cursor-not-allowed" />
                                            </div>
                                            <div className="space-y-2">
                                                <label className="text-[10px] font-black uppercase text-white/40">Serie Facturas</label>
                                                <input type="text" value="CP-2024-" disabled className="w-full bg-white/5 border border-white/10 rounded-xl py-2 px-4 text-white/40 text-sm italic cursor-not-allowed" />
                                            </div>
                                        </div>
                                        <p className="text-xs text-white/40 bg-white/5 p-4 rounded-xl border border-white/5 leading-relaxed">
                                            Los cambios fiscales deben ser aprobados por el departamento contable central.
                                        </p>
                                    </div>
                                </CardGlass>
                            </div>
                        )}
                    </div>
                )}
            </div>
        </SuperAdminLayout>
    );
}

const InfoCircle = ({ className, size }) => (
    <div className={`${className} flex items-center justify-center`} style={{ width: size, height: size }}>
        <AlertCircle size={size} />
    </div>
);
